import {
	attach,
	Effect,
	Store,
	is,
	createDomain,
	Event,
	createStore,
	combine,
} from 'effector'
import { FetchType, AnyObject, FetchErrorType } from '../types'
import { pipe, withUrl, withDev, withParams, isDevelopment } from '../utils'
import { buildFormData } from '../fetch'
import { StringifyOptions } from 'query-string'

export type RequestEffectSetting<
	Data extends AnyObject | string | void,
	Params extends AnyObject | void
> = {
	url: string | Store<string>
	method?: 'GET' | 'POST'
	params?: Store<Params> | Params
	data?: Store<Data> | Data
	dev?: boolean
	abort?: boolean
	settingWithParams?: StringifyOptions
	cache?: boolean
}

export type cacheType = { [k: string]: AnyObject }

export interface RequestEffect<Params, Done, Fail = FetchErrorType>
	extends Effect<Params, Done, Fail> {
	abort?: Event<void>
}

export const makeRequest = async <T extends any = any>({
	url,
	params = {},
	data = {},
	method = 'GET',
	responseHandler: _responseHandler,
}: {
	url: string
	params?: AnyObject
	data?: AnyObject
	method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
	responseHandler?: (response: Response) => Promise<T>
}) => {
	const responseHandler = async (response: Response) => {
		if (!_responseHandler) return await response.json()

		try {
			const value = await _responseHandler(response)
			return { data: value, success: true }
		} catch (e) {
			return { error: e }
		}
	}

	const headers: { [k: string]: any } = {}
	if (localStorage.getItem('access_token') && isDevelopment) {
		headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
	}
	if (isDevelopment && process.env.REACT_APP_API_KEY) {
		headers['X-Api-Key'] = process.env.REACT_APP_API_KEY
	}
	const createdUrl = params
		? pipe(s => s, withUrl(), withParams(params))(url)
		: pipe(s => s, withUrl())

	let res: FetchType<T>
	if (method === 'GET') {
		res = await fetch(createdUrl, {
			headers,
		}).then(responseHandler)
	} else {
		// eslint-disable-next-line no-undef
		let fd: BodyInit

		if (typeof data !== 'string') {
			fd = new FormData()
			buildFormData(fd, data)
		} else {
			fd = data
		}

		res = await fetch(createdUrl, {
			method,
			body: fd,
			headers,
		}).then(responseHandler)
	}

	const { success } = res

	if (!success) {
		throw res.error
	}

	return res.data
}

function createRequestEffect<
	Data extends AnyObject | string | void,
	Params extends AnyObject | void,
	Done extends AnyObject = AnyObject,
	E2EPayload extends AnyObject = AnyObject
>(
	_p: RequestEffectSetting<Data, Params> & {
		payload?: E2EPayload
		headers?: { [k: string]: string }
	},
	domain = createDomain()
) {
	const {
		url,
		method: methodInit = 'GET',
		params,
		data,
		dev,
		abort = true,
		settingWithParams = { arrayFormat: 'bracket' },
		cache: cache_ = false,
		payload = {},
		headers: _headers = {},
	} = _p
	const { createStore, createEffect, createEvent } = domain

	const $cache = createStore<cacheType>({})
	const setCache = createEvent<cacheType>()
	$cache.on(setCache, (store, payload) => Object.assign(store, payload))

	type EffectPT = {
		url: string
		params: Params
		data: Data
		method: 'GET' | 'POST'
		cache: cacheType
	}
	const fx = createEffect<EffectPT, Done>()
	let controller = new AbortController()

	fx.use(
		async ({ url, params = {}, data = {}, method: methodParams, cache }) => {
			const headers: { [k: string]: any } = {
				..._headers,
			}
			if (localStorage.getItem('access_token') && isDevelopment) {
				headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
			}
			if (isDevelopment && process.env.REACT_APP_API_KEY) {
				headers['X-Api-Key'] = process.env.REACT_APP_API_KEY
			}

			const method = methodParams || methodInit
			if (abort) controller?.abort()
			controller = new AbortController()

			const createdUrl = pipe(
				dev ? withDev : s => s,
				withUrl(),
				withParams(params, settingWithParams)
			)(url)

			if (cache && createdUrl in cache && cache_) {
				return cache[createdUrl]
			}

			let res: FetchType
			if (method === 'GET') {
				res = await fetch(createdUrl, {
					signal: controller.signal,
					headers,
				}).then(x => x.json())
			} else {
				// eslint-disable-next-line no-undef
				let fd: BodyInit

				if (typeof data !== 'string') {
					fd = new FormData()
					buildFormData(fd, data)
				} else {
					fd = data
				}

				res = await fetch(createdUrl, {
					signal: controller.signal,
					method,
					body: fd,
					headers,
				}).then(x => x.json())
			}

			if (url.indexOf('api/profile/config') > -1) {
				return res
			}

			const { success } = res

			if (!success) {
				throw res.error
			}

			setCache({ [createdUrl]: res.data })
			return res.data
		}
	)

	const $url = is.store(url) ? url : createStore(url)
	const $params = is.store(params)
		? params
		: createStore(params || ({} as Params))
	const $data = is.store(data) ? data : createStore(data || ({} as Data))

	const attachFx = () => {
		return attach<
			void | {
				url?: string
				params?: Params
				data?: Data
				cache?: cacheType
				method?: 'GET' | 'POST'
			},
			{
				url: Store<string>
				params: Store<Params>
				data: Store<Data>
				cache: Store<cacheType>
			},
			any
		>({
			source: {
				url: $url,
				params: $params,
				data: $data,
				cache: $cache,
			},
			effect: fx,
			mapParams: (p, s) => {
				if (p)
					return {
						url: p.url || s.url,
						params: {
							...(s.params || {}),
							...(p.params || {}),
						},
						data:
							typeof p.data === 'string' || typeof s.data === 'string'
								? p.data
								: {
										...((s.data as AnyObject) || {}),
										...((p.data as AnyObject) || {}),
										// eslint-disable-next-line no-mixed-spaces-and-tabs
								  },
						method: p.method,
						cache: p.cache,
						payload,
					}
				return s
			},
		})
	}

	const returnAttachFx = attachFx() as RequestEffect<
		void | {
			url?: string
			params?: Params
			data?: Data
			method?: 'GET' | 'POST'
			payload?: E2EPayload
		},
		Done,
		FetchErrorType
	>

	const abortEvent = createEvent()
	abortEvent.watch(() => controller.abort())
	returnAttachFx.abort = abortEvent
	return returnAttachFx
}

const makeIdleStatus = <Done extends AnyObject = AnyObject>(
	fx: RequestEffect<AnyObject | void, Done, FetchErrorType>
) => {
	const $wasInit = createStore(false).on(fx, () => true)

	return combine(
		{ pending: fx.pending, wasInit: $wasInit },
		({ pending, wasInit }) =>
			wasInit ? (pending ? 'loading' : 'done') : 'initial'
	)
}

export { createRequestEffect, makeIdleStatus }
